import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ShipmentStatusBadge } from "components/ShipmentStatusBadge";
import { ShipmentCustomNameText } from "components/Texts/ShipmentCustomNameText";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import {
  convertSubdivisionCodeToSimplifiedSubdivisionCode,
  formatText,
} from "utils";

import { EVOTRUX_BLUE, EVOTRUX_TEAL } from "../../../../../constants";

interface Props {
  supplierShipment: SupplierShipment;
  selected: boolean;
  onShipmentSelected: (id: number) => void;
  selectionIsDisabled: boolean;
}

export function CompactSupplierShipmentPreview(props: Props) {
  const navigate = useNavigate();
  const {
    supplierShipment,
    selected,
    onShipmentSelected,
    selectionIsDisabled,
  } = props;
  const shipmentInfo = supplierShipment.info;
  const firstPointLocation = supplierShipment?.first_point?.point_location;
  const lastPointLocation = supplierShipment?.last_point?.point_location;

  const handleClick = () => {
    if (!selectionIsDisabled) {
      navigate(`/supplier-shipments/${shipmentInfo.id}`);
      onShipmentSelected(shipmentInfo.id);
    }
  };

  return (
    <Box
      display="flex"
      px="1rem"
      py="0.75rem"
      sx={{
        "&:hover": {
          bgcolor: selected ? undefined : "#f8f8f8",
          cursor: selectionIsDisabled ? "default" : "pointer",
        },
        bgcolor: selected ? "#e3f2fd" : "transparent",
        borderLeft: selected
          ? `4px solid ${EVOTRUX_BLUE}`
          : `4px solid ${EVOTRUX_TEAL}`,
        opacity: selectionIsDisabled ? 0.6 : 1,
      }}
      onClick={handleClick}
    >
      <Stack direction="column" spacing={0.5} flex={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" fontWeight="bold">
            ID: {shipmentInfo.id}
          </Typography>
          <ShipmentStatusBadge
            status={supplierShipment?.shipment_status}
            size="small"
          />
        </Stack>

        {supplierShipment.info.custom_name && (
          <ShipmentCustomNameText data={supplierShipment.info.custom_name} />
        )}

        <Typography variant="body2" color={EVOTRUX_TEAL} fontWeight="bold">
          From: {supplierShipment.supplier_company.name}
        </Typography>

        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {formatText(shipmentInfo.commodity_freetext ?? "General Freight")}
        </Typography>

        <Stack direction="column" spacing={0.25}>
          <ShipmentLocationText
            data={[
              firstPointLocation?.city,
              firstPointLocation?.province
                ? convertSubdivisionCodeToSimplifiedSubdivisionCode(
                    firstPointLocation.province,
                  )
                : "",
              firstPointLocation?.postal,
              firstPointLocation?.country,
            ]
              .filter(Boolean)
              .join(", ")}
            icon={
              <TripOriginIcon sx={{ fontSize: "0.8rem", marginRight: "4px" }} />
            }
          />
          <Box mx="0.5rem">
            <Divider />
          </Box>
          <ShipmentLocationText
            data={[
              lastPointLocation?.city,
              lastPointLocation?.province
                ? convertSubdivisionCodeToSimplifiedSubdivisionCode(
                    lastPointLocation.province,
                  )
                : "",
              lastPointLocation?.postal,
              lastPointLocation?.country,
            ]
              .filter(Boolean)
              .join(", ")}
            icon={
              <FmdGoodIcon sx={{ fontSize: "0.8rem", marginRight: "4px" }} />
            }
          />
        </Stack>
      </Stack>
    </Box>
  );
}
